/**
 * MapCanvas Component
 *
 * The MapCanvas component integrates Mapbox and Three.js to create interactive 3D elements within the Mapbox canvas context.
 * It utilizes the React-Map-GL library for Mapbox integration and React-Three-Map & React-Three-Fiber for Three.js integration.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import styled from 'styled-components';
import MapboxGl from 'mapbox-gl';
import { Map } from 'react-map-gl/mapbox';
import { Canvas } from 'react-three-map';
import { useThree } from '@react-three/fiber';
import type { Scene } from 'three';

// Types
import { TwinEntity } from '@repo/backend-types';

// Context
import { useTwinContext, useUserStore, useModeContext } from '@repo/front-end-state';

// Types
import { AppMode } from '@repo/frontend-types';

// Hooks & Utils
import {
  readCameraStart,
  resolveMapLocationAndCamera,
  toInitialViewState,
  flyToTarget,
  FlightInterrupted,
} from '@repo/ui';
import { getMapStyleUrl } from '@repo/front-end-utils';

// Config
import Config from '../../common/Config';

// Stores
import { useDeveloperStore, ConfigurationSource, useTwinModelStore } from '@repo/front-end-state';

/**
 * Publishes the active Three.js scene to TwinModelStore.sceneRef so non-r3f
 * code (notably flyToTarget's computeEntityPose) can traverse the scene to
 * read mesh world-space bounding boxes. Renders nothing.
 */
const SceneRefPublisher = () => {
  const scene = useThree(state => state.scene as Scene);
  useEffect(() => {
    const { sceneRef } = useTwinModelStore.getState();
    sceneRef.current = scene;
    return () => {
      if (sceneRef.current === scene) {
        sceneRef.current = null;
      }
    };
  }, [scene]);
  return null;
};

// Components
import { TwinScene } from './TwinScene';
import { PlanViewContext, type PlanViewContextValue } from './planView/PlanHeightScale';
import { liveEditFlightScene } from './planView/liveEditFlightScene';
import { PLAN_REST_HEIGHT_SCALE } from './planView/planMapLock';
import { prefersReducedMotion } from './planView/planHandover';
import {
  arrivalHeightScale,
  createArrivalProgress,
  takeLiveArrival,
  type LiveArrival,
  type ViewCamera,
} from './planView/liveArrival';

// CSS
import 'mapbox-gl/dist/mapbox-gl.css';
import { TwinMarkers } from '@repo/ui';

type Props = {
  onAllModelsLoaded: () => void;
};

// Full-viewport map layer (was Tailwind `fixed top-0 left-0 w-lvw h-lvh opacity-100 pointer-events-auto`).
const MapFill = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100lvw;
  height: 100lvh;
  opacity: 1;
  pointer-events: auto;
`;

/** The map's camera, in view-state shape. */
const cameraOf = (map: MapboxMap): ViewCamera => {
  const centre = map.getCenter();
  return {
    longitude: centre.lng,
    latitude: centre.lat,
    zoom: map.getZoom(),
    pitch: map.getPitch(),
    bearing: map.getBearing(),
  };
};

const toViewCamera = (view: {
  longitude: number;
  latitude: number;
  zoom?: number;
  pitch?: number;
  bearing?: number;
}): ViewCamera => ({
  longitude: view.longitude,
  latitude: view.latitude,
  zoom: view.zoom ?? 0,
  pitch: view.pitch ?? 0,
  bearing: view.bearing ?? 0,
});

/**
 * Development builds only: the launch-into-live rig's probe (spec criteria 4, 6 and 12). The live
 * map's camera when it loaded, its camera and the twin's height scale at every frame it renders
 * (capped), and its camera now, read by `tests/launchIntoLive.ab.pw.spec.ts`. Never in a production
 * build; holds no twin data.
 */
const installLiveMapProbe = (map: MapboxMap, heightScale: () => number) => {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return;
  const frames: Array<ViewCamera & { heightScale: number }> = [];
  const probe = {
    loaded: cameraOf(map),
    frames,
    now: () => cameraOf(map),
  };
  map.on('render', () => {
    if (frames.length < 2_000) frames.push({ ...cameraOf(map), heightScale: heightScale() });
  });
  (window as unknown as Record<string, unknown>).__liveMapProbe = probe;
};

const MapCanvas = ({ onAllModelsLoaded }: Props) => {
  const { authMetadata } = useUserStore();
  const { twin, mapRef: storeMapRef } = useTwinContext(); // Access the Twin Context to retrieve twin model information.
  const { setMapOrigin } = useDeveloperStore();
  const { appMode } = useModeContext();

  const infoRef = useRef<HTMLDivElement>(null); // just for debug
  const mapRef = useRef<any>(null); // to access internals of react-map-gl
  // This canvas's own map once it has loaded (`spec-onboarding-persistent-scene-launch-into-live`,
  // R5, R6): the twin flight flies it, and the unmount clears the shared `mapRef` only if it is it.
  const loadedMapRef = useRef<MapboxMap | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  /*
   * The live arrival (launch-into-live R3): taken once, at the first render, and only for the twin
   * the viewer holds. Present, the map opens at Launch's plan view, flat, and the first-load flight
   * carries it to the twin's opening view, the machines growing to full height on the way (R4).
   */
  const [arrival] = useState<LiveArrival | null>(() =>
    takeLiveArrival(useTwinModelStore.getState().twinBId),
  );
  // R4: the live twin's height while the arrival runs (below 1), else 1 once it is over or when
  // none ran. Read every frame by the height group and the machine look; never state, so the flight
  // re-renders nothing.
  const heightScaleRef = useRef(arrival ? PLAN_REST_HEIGHT_SCALE : 1);
  // The live scene's height source. While an arrival runs it reports the arrival's height; otherwise
  // it is the Edit flight's (`spec-onboarding-persistent-scene-edit-flight`, R4), full height and
  // `off` at rest, so `TwinScene`, which takes the Edit flight's only where no plan view is
  // provided, still flattens the twin as Edit lands.
  const planView = useMemo<PlanViewContextValue>(
    () => ({
      getHeightScale: () =>
        heightScaleRef.current < 1 ? heightScaleRef.current : liveEditFlightScene.getHeightScale(),
      getState: () => (heightScaleRef.current < 1 ? 'off' : liveEditFlightScene.getState()),
    }),
    [],
  );

  const { mapOrigin, initialViewState: resolvedViewState } = useMemo(() => {
    // In ORG_SELECTION mode, ignore twin data to force organization-level camera settings
    const effectiveTwin = appMode === AppMode.ORG_SELECTION ? null : twin;

    const resolved = resolveMapLocationAndCamera({
      twin: effectiveTwin,
      authMetadata,
    });

    const twinOrigin = resolved.twinLocation || resolved.location; // Use twin location for 3D model positioning
    const viewState = toInitialViewState(resolved);

    return { mapOrigin: twinOrigin, initialViewState: viewState };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the physical model's map-origin coordinates are listed so an in-place move of the model re-resolves
  }, [
    twin,
    authMetadata,
    twin?.physicalModel?.mapOrigin?.latitude,
    twin?.physicalModel?.mapOrigin?.longitude,
    appMode,
  ]);

  // The map's first view: the arrival's plan view when one was taken (R3.1), else the resolver's.
  // react-map-gl reads it only when the map is created.
  const initialViewState = arrival
    ? {
        longitude: arrival.center[0],
        latitude: arrival.center[1],
        zoom: arrival.zoom,
        bearing: arrival.bearing,
        pitch: 0,
      }
    : resolvedViewState;

  // R6: a later reader of the shared `mapRef` finds no map, never this removed one. Only if it
  // still holds this canvas's map: `TwinScene` writes the same map, and a newer canvas may own it.
  useEffect(
    () => () => {
      const { mapRef: shared } = useTwinModelStore.getState();
      if (loadedMapRef.current && shared.current === loadedMapRef.current) shared.current = null;
    },
    [],
  );

  // Update developer store for debugging/tracking - moved to useEffect to avoid setState during render
  useEffect(() => {
    if (twin?.adminTwin && readCameraStart(twin.adminTwin.camera_start_args)) {
      setMapOrigin(
        mapOrigin,
        ConfigurationSource.TWIN_ADMIN,
        `Camera view position: ${twin.adminTwin.bid}`,
      );
    } else if (twin?.adminTwin?.latitude && twin?.adminTwin?.longitude) {
      setMapOrigin(
        mapOrigin,
        ConfigurationSource.TWIN_ADMIN,
        `Twin adminTwin: ${twin.adminTwin.bid}`,
      );
    } else if (twin?.physicalModel?.mapOrigin) {
      setMapOrigin(
        mapOrigin,
        ConfigurationSource.TWIN_PHYSICAL_MODEL,
        `Twin physicalModel: ${twin.adminTwin?.bid || 'unknown'}`,
      );
    } else if (authMetadata?.orgLatitude && authMetadata?.orgLongitude) {
      setMapOrigin(
        mapOrigin,
        ConfigurationSource.ORGANIZATION,
        `Org: ${authMetadata?.organisation}`,
      );
    } else {
      setMapOrigin(mapOrigin, ConfigurationSource.DEFAULT_BATTERSEA, 'Default Battersea Park');
    }
  }, [
    mapOrigin,
    twin,
    authMetadata,
    setMapOrigin,
    twin?.physicalModel?.mapOrigin?.latitude,
    twin?.physicalModel?.mapOrigin?.longitude,
  ]);

  const [twinRootEntity, setTwinRootEntity] = useState<TwinEntity | null>(null);

  // Diagnostic: log MapCanvas render state. Pairs with [TwinScene mount/
  // unmount] and [TwinScene frames] logs to trace what's happening during
  // a fly-to-org. shouldRenderTwinScene mirrors the IIFE conditional below.
  const shouldRenderTwinSceneDiag = !!twin && !!twinRootEntity && appMode !== AppMode.ORG_SELECTION;
  // eslint-disable-next-line no-console
  console.debug(
    '[MapCanvas render] appMode=',
    appMode,
    'mapOrigin=',
    mapOrigin ? `${mapOrigin.latitude.toFixed(4)},${mapOrigin.longitude.toFixed(4)}` : null,
    'twinId=',
    twin?.adminTwin?.bid ?? null,
    'twinRootEntity=',
    !!twinRootEntity,
    'shouldRenderTwinScene=',
    shouldRenderTwinSceneDiag,
  );

  // Track previous mode to detect mode changes
  const prevModeRef = useRef<AppMode | null>(null);

  // Handle explicit mode transitions
  useEffect(() => {
    const prevMode = prevModeRef.current;

    if (prevMode !== null && prevMode !== appMode) {
      // When switching modes, clear 3D scene and reset camera constraints
      if (appMode === AppMode.ORG_SELECTION) {
        // Force clear twinRootEntity immediately
        setTwinRootEntity(null);

        // Reset zoom limits and clear twin bounds before flying. flyToTarget
        // also calls setMaxBounds(null) defensively; doing it here too keeps
        // the mode-transition side-effects co-located. (CP7 removed the
        // TwinScene camera-store subscription that previously
        // re-clamped ±1000m bounds on every camera update, so this is now
        // purely defence-in-depth rather than racing against another writer.)
        if (mapRef.current) {
          const map = mapRef.current.getMap();
          if (map) {
            map.setMinZoom(6);
            map.setMaxZoom(22);
            map.setMaxBounds(null);
            map.setRenderWorldCopies(true);
          }
        }

        // Fly to the org HQ via the unified flyToTarget. Replaces the
        // old camera-store fly + setTimeout(100ms) race. The 100ms
        // hand-tuned delay was working around a bounds-then-flyTo race that
        // doesn't exist with flyToTarget (which calls map.stop() and clears
        // bounds synchronously before flyTo).
        if (authMetadata?.orgBID) {
          flyToTarget(authMetadata.orgBID).catch((err: unknown) => {
            if (err instanceof FlightInterrupted) return;
            // eslint-disable-next-line no-console
            console.error('[MapCanvas] flyToTarget(org) failed', err);
          });
        }
      }

      // When switching to TWIN_SELECTION, also clear 3D scene and reset constraints
      else if (appMode === AppMode.TWIN_SELECTION) {
        // Force clear twinRootEntity to ensure clean slate for new twin
        setTwinRootEntity(null);

        // Reset map constraints to defaults (new twin will apply its own if needed).
        // CP7 (#5): the 50ms setTimeout that previously wrapped this block was
        // working around the TwinScene cameraPosition subscription racing to
        // re-apply bounds. That subscription is gone — running synchronously.
        if (mapRef.current) {
          const map = mapRef.current.getMap();
          if (map) {
            map.setMaxBounds(null);
            map.setMinZoom(6);
            map.setMaxZoom(22);
          }
        }
      }
    }

    prevModeRef.current = appMode;
  }, [appMode, authMetadata]);

  // Fly to the loaded twin via the unified flyToTarget when the twin changes.
  //
  // Once both the map has loaded and the twin has loaded, whichever comes second
  // (`spec-onboarding-persistent-scene-launch-into-live`, R5): a twin loaded before the map used to
  // be marked flown and never flown (`flyToTarget` threw "map not ready"). It flies this canvas's
  // own map, never whatever `TwinModelStore.mapRef` holds (R5.1), and a BID is recorded as flown
  // only once its flight is dispatched (R5.2).
  //
  // CP6c: when useSpotlight pre-dispatched a flight for this BID (cross-twin
  // spotlight), the camera is already mid-flight (or settled) at the new
  // twin's saved pose — re-dispatching here would jump-cut. We skip the
  // dispatch when prefetchedTwinFlightBid matches the newly-loaded BID, but
  // still bump prevTwinBidRef so subsequent twin changes follow the normal
  // path.
  const prevTwinBidRef = useRef<string | null>(null);
  // The arrival's flight is the first twin flight, for the arrival's own twin, at most once.
  const arrivalFlownRef = useRef(false);
  useEffect(() => {
    const newBid = twin?.adminTwin?.bid ?? null;
    if (!newBid) return;
    if (prevTwinBidRef.current === newBid) return;
    const map = loadedMapRef.current;
    if (!mapLoaded || !map) return;
    prevTwinBidRef.current = newBid;

    const { prefetchedTwinFlightBid, setPrefetchedTwinFlightBid } = useTwinModelStore.getState();
    if (prefetchedTwinFlightBid === newBid) {
      // eslint-disable-next-line no-console
      console.debug(
        '[MapCanvas] skipping flyToTarget — predictive dispatch already covered bid=',
        newBid,
      );
      setPrefetchedTwinFlightBid(null);
      endArrival(map);
      return;
    }

    const arriving = !!arrival && !arrivalFlownRef.current && arrival.twinBid === newBid;
    arrivalFlownRef.current = true;
    let onMove: (() => void) | null = null;
    let options: { map: MapboxMap; duration?: number } = { map };
    if (arriving) {
      const opening = toViewCamera(
        toInitialViewState(resolveMapLocationAndCamera({ twin, authMetadata })),
      );
      if (prefersReducedMotion()) {
        // R4.3: the camera jumps and the twin is at full height at once. The twin path of
        // `flyToTarget` does not read reduced motion itself, and even a 1 ms flight can draw one
        // blended frame when its first tick lands inside that millisecond. So the camera jumps to
        // the opening view first, and the 1 ms flight (0 would divide by zero in its animator) then
        // has nowhere left to go.
        heightScaleRef.current = 1;
        try {
          map.jumpTo({
            center: [opening.longitude, opening.latitude],
            zoom: opening.zoom,
            pitch: opening.pitch,
            bearing: opening.bearing,
          });
        } catch {
          // The map is being removed; the flight below settles as it would.
        }
        options = { map, duration: 1 };
      } else {
        // R4.1: the height follows how far the camera has travelled, eased.
        const progress = createArrivalProgress(cameraOf(map), opening);
        onMove = () => {
          heightScaleRef.current = arrivalHeightScale(
            progress(cameraOf(map)),
            PLAN_REST_HEIGHT_SCALE,
          );
        };
        map.on('move', onMove);
      }
    } else {
      endArrival(map);
    }

    const settle = () => {
      if (onMove) map.off('move', onMove);
      if (arriving) endArrival(map);
    };
    flyToTarget(newBid, options).then(settle, (err: unknown) => {
      // R4.1: exactly full height when the flight is interrupted or fails, as when it lands.
      settle();
      if (err instanceof FlightInterrupted) return;
      // eslint-disable-next-line no-console
      console.error('[MapCanvas] flyToTarget failed', { twinBid: newBid, arriving }, err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the flight is keyed on the twin and the map's load; the arrival is fixed at mount
  }, [twin, mapLoaded]);

  /** R4.1, R4.2: the arrival is over, or never ran: full height, drawn. */
  function endArrival(map: MapboxMap) {
    if (heightScaleRef.current === 1) return;
    heightScaleRef.current = 1;
    try {
      // The canvas draws on demand: the last height needs a frame.
      map.triggerRepaint();
    } catch {
      // The map is being removed.
    }
  }

  // Fly to the new organisation when authMetadata.orgBID changes (i.e. user
  // picks a different org from the OrganisationSelector). The selector flow
  // does setAuthMetadata(newOrg) → setAppMode(TWIN_SELECTION), so the mode-
  // transition effect's TWIN_SELECTION branch doesn't fly anywhere — without
  // this effect, the camera stays put. (Legacy code triggered this fly
  // indirectly via its mapOrigin-change useEffect, which was removed in
  // Checkpoint 3.)
  const prevOrgBidRef = useRef<string | null>(null);
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug(
      '[org-effect] fire: orgBID=',
      authMetadata?.orgBID,
      'prev=',
      prevOrgBidRef.current,
    );

    const newOrgBid = authMetadata?.orgBID ?? null;
    if (!newOrgBid) return;

    // First call after mount: just record the initial org BID without flying.
    // Map's initialViewState handles positioning on app load.
    if (prevOrgBidRef.current === null) {
      // eslint-disable-next-line no-console
      console.debug('[org-effect] initial mount, recording orgBID, no fly');
      prevOrgBidRef.current = newOrgBid;
      return;
    }

    if (prevOrgBidRef.current === newOrgBid) {
      // eslint-disable-next-line no-console
      console.debug('[org-effect] same orgBID, no fly');
      return;
    }
    // eslint-disable-next-line no-console
    console.debug('[org-effect] orgBID changed, calling flyToTarget(', newOrgBid, ')');
    prevOrgBidRef.current = newOrgBid;

    flyToTarget(newOrgBid).catch((err: unknown) => {
      if (err instanceof FlightInterrupted) return;
      // eslint-disable-next-line no-console
      console.error('[MapCanvas] flyToTarget(org) on org change failed', err);
    });
  }, [authMetadata?.orgBID]);

  if (!authMetadata) {
    throw new Error('this wont happen, the router will navigate to login if authCreds are null');
  }

  // Resolve the Mapbox token: env config first, Auth0 claim fallback, else ''
  // (map stays hidden when empty, exactly as before).
  const mapboxToken = useMemo(() => {
    return Config.mapboxToken || authMetadata.tokens?.mapboxToken || '';
  }, [authMetadata.tokens]);

  MapboxGl.accessToken = mapboxToken;

  // Detect device and browser capabilities for compatibility and performance optimizations.
  // WebGL2, VRAM, and potential future WebGPU build considerations.
  // const gl2 = WebGL.isWebGL2Available();

  useEffect(() => {
    // Only set twin entity in modes that should show 3D twins (not in ORG_SELECTION)
    if (appMode !== AppMode.ORG_SELECTION && twin?.physicalModel && mapOrigin) {
      setTwinRootEntity(twin.physicalModel);
    } else {
      setTwinRootEntity(null);
    }
  }, [twin, mapOrigin, appMode]);

  if (!mapOrigin) {
    return null;
  } else {
    return (
      <>
        {/* {Config.appProfile === "DEBUG" && (
                    <DebugGlInfoDiv infoRef={infoRef} />
                )} */}

        <MapFill>
          {/* Check for Mapbox token and twin model existence. */}
          {mapboxToken && (
            <Map
              ref={mapRef}
              antialias
              minZoom={6}
              maxZoom={22}
              initialViewState={initialViewState}
              onLoad={() => {
                // Populate the shared TwinModelStore mapRef with the live mapboxgl.Map
                // instance so flyToTarget (and future code) can reach it without prop-drilling.
                const m = mapRef.current?.getMap();
                if (m) {
                  storeMapRef.current = m;
                  loadedMapRef.current = m;
                  installLiveMapProbe(m, () => planView.getHeightScale());
                  setMapLoaded(true);
                }
              }}
              /*
               * useWebGL2 is now the default for mapbox but we may want to use WebGL1 for older device/browser support.
               * Additionally, consider potential future webGPU build.
               * https://caniuse.com/webgl2 ( Global Support 95.89% ) 09/03/2024
               * https://caniuse.com/webgpu ( Global Support 25.09% ) 09/03/2024
               */
              // useWebGL2={gl2}

              /*
               * Set MapBox style URL for the base map visualization style.
               */
              mapStyle={getMapStyleUrl(appMode, Config.mapboxStyleUrl)}
            >
              {/*
               * Use the React-Three-Map canvas component to render R3F 3D components.
               */}
              <Canvas
                frameloop="demand"
                latitude={mapOrigin.latitude}
                longitude={mapOrigin.longitude}
                overlay={true}
                flat
                shadows
                performance={{
                  min: 0.3,
                  max: 0.4,
                }}
                // Override react-three-map's default `autoClear: false` for
                // the overlay canvas. Without this, the Three canvas accumulates
                // pixels across frames — so when TwinScene's content effectively
                // disappears from the React tree (e.g. on a fly-to-org), the
                // previous twin's render persists frozen on the overlay while
                // Mapbox repaints underneath. Setting autoClear: true makes the
                // renderer clear before each frame, letting the twin recede
                // correctly as the camera flies away (or wink out cleanly if
                // its scene is empty).
                gl={{ autoClear: true }}
                onCreated={() => {
                  // Do nothing atm
                }}
              >
                {/* <axesHelper args={[100]}/> */}
                <SceneRefPublisher />
                {(() => {
                  // Only render TwinScene when we have twin data AND not in ORG_SELECTION mode
                  const shouldRenderTwinScene =
                    twin && twinRootEntity && appMode !== AppMode.ORG_SELECTION;

                  if (shouldRenderTwinScene) {
                    const resolvedCamera = resolveMapLocationAndCamera({
                      twin,
                      authMetadata,
                    });

                    return (
                      // The arrival's height (launch-into-live R4), through the plan view's own
                      // context: `TwinScene`'s height group and machine look already read it. A
                      // stable value, so `TwinScene`'s memo holds.
                      <PlanViewContext.Provider value={planView}>
                        <TwinScene
                          cameraStart={twin.adminTwin.camera_start_args}
                          infoRef={infoRef}
                          twinRootEntity={twinRootEntity}
                          onAllModelsLoaded={onAllModelsLoaded}
                          mapOrigin={mapOrigin}
                          resolvedCamera={resolvedCamera}
                        />
                      </PlanViewContext.Provider>
                    );
                  }
                  return null;
                })()}
              </Canvas>
              {/* Twin location markers - only shown in TWIN_SELECTION mode */}
              <TwinMarkers middlewareApi={Config.middlewareApi} />
            </Map>
          )}
          {/* Debug overlay - comment/uncomment as needed for debugging */}
          {/* <MapDebugOverlay mapOrigin={mapOrigin} /> */}
        </MapFill>
      </>
    );
  }
};

export { MapCanvas };
